import React, { useCallback, useMemo } from 'react'

import { Box, Checkbox, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { getSegmentValue } from 'components/SegmentSelector/segment-service'

import { withContext } from '../Context'
import Range from './Range'

const useStyles = makeStyles((theme) => ({
  icon: {
    minWidth: theme.spacing(5),
  },
  border: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
  },
  expanded: {
    display: 'block',
  },
  collapsed: {
    display: 'none',
  },
}))

export type Subject = {
  name: string
  label: string
  min: number
  max: number
  rangeStep: number
}

export type TestScoreSelectorWithSubjectsProps = {
  criteria: string
  onChange: (criteria: string, segmentName: string, value: any, operation: string) => void
  segment: object
  isSubjectTestsEnabled?: boolean
  testLabel: string
  totalScoreSegmentName: string
  totalScoreMin: number
  totalScoreMax: number
  totalScoreStep?: number
  subjects: Subject[]
}

const OPERATION = 'between'
export const TestScoreSelectorWithSubjects = ({
  criteria = 'OneOf',
  onChange,
  segment,
  testLabel,
  isSubjectTestsEnabled,
  totalScoreSegmentName,
  totalScoreMin,
  totalScoreMax,
  totalScoreStep = 1,
  subjects,
}: TestScoreSelectorWithSubjectsProps) => {
  const classes = useStyles()
  const [checkedStates, setCheckedStates] = React.useState<Record<string, boolean>>(() => {
    return {
      ...subjects.reduce(
        (acc, subject) => {
          const currentValue = getSegmentValue(segment, criteria, subject.name)
          acc[subject.name] = currentValue.length > 0
          return acc
        },
        {} as Record<string, boolean>,
      ),
      [totalScoreSegmentName]: getSegmentValue(segment, criteria, totalScoreSegmentName).length > 0,
    }
  })

  const [rangeValues, setRangeValues] = React.useState(() => {
    const initialValues: Record<string, { from: number; to: number }> = {}

    const totalScoreValue = getSegmentValue(segment, criteria, totalScoreSegmentName)
    initialValues[totalScoreSegmentName] = {
      from: totalScoreValue.length > 0 ? totalScoreValue[0] : totalScoreMin,
      to: totalScoreValue.length > 0 ? totalScoreValue[1] : totalScoreMax,
    }

    subjects.forEach((subject) => {
      const value = getSegmentValue(segment, criteria, subject.name)
      initialValues[subject.name] = {
        from: value.length > 0 ? value[0] : subject.min,
        to: value.length > 0 ? value[1] : subject.max,
      }
    })

    return initialValues
  })

  // Check if any segment is selected (total score or any subject)
  const isAnySegmentSelected = useMemo(() => {
    return checkedStates[totalScoreSegmentName] || Object.values(checkedStates).some((checked) => checked)
  }, [checkedStates])

  const toggleSegmentValue = useCallback(
    (value: number[], segmentName: string) => {
      onChange(criteria, segmentName, value, OPERATION)
    },
    [onChange, criteria],
  )

  const handleCheckboxChange = (subjectName: string, checked: boolean) => {
    const currentValues = rangeValues[subjectName]
    const value = checked ? [currentValues.from, currentValues.to] : []

    setCheckedStates((prev) => ({
      ...prev,
      [subjectName]: checked,
    }))

    toggleSegmentValue(value, subjectName)
  }

  const handleRangeChange = (subjectName: string, value: number[]) => {
    setRangeValues((prev) => ({
      ...prev,
      [subjectName]: { from: value[0], to: value[1] },
    }))
    onChange(criteria, subjectName, [value[0], value[1]], OPERATION)
  }

  return (
    <Box
      my={2}
      className={classes.border}
    >
      <ListItem
        role="listitem"
        aria-label={`Select ${testLabel}`}
      >
        <ListItemIcon className={classes.icon}>
          <Checkbox
            edge="start"
            color="primary"
            checked={checkedStates[totalScoreSegmentName]}
            disableRipple={false}
            onChange={(e) => handleCheckboxChange(totalScoreSegmentName, e.target.checked)}
          />
        </ListItemIcon>
        <ListItemText primary={<Typography variant="body1">{testLabel}</Typography>} />
      </ListItem>

      <Box className={isAnySegmentSelected ? classes.expanded : classes.collapsed}>
        <Box px={3}>
          <Range
            step={totalScoreStep}
            min={totalScoreMin}
            max={totalScoreMax}
            disabled={!checkedStates[totalScoreSegmentName]}
            from={rangeValues[totalScoreSegmentName]?.from || totalScoreMin}
            to={rangeValues[totalScoreSegmentName]?.to || totalScoreMax}
            getAriaValueText={(value: number) => `Total range: ${value}`}
            onChange={(value: number[]): void => {
              handleRangeChange(totalScoreSegmentName, value)
            }}
          />
        </Box>
        {isSubjectTestsEnabled &&
          subjects.map((subject) => {
            const currentValue = rangeValues[subject.name]
            const isSubjectChecked = checkedStates[subject.name]

            return (
              <Box key={subject.name}>
                <ListItem
                  role="listitem"
                  aria-label={`Select ${subject.label}`}
                >
                  <ListItemIcon className={classes.icon}>
                    <Checkbox
                      edge="start"
                      color="primary"
                      checked={isSubjectChecked}
                      onChange={(e) => handleCheckboxChange(subject.name, e.target.checked)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={<Typography variant="body1">{subject.label}</Typography>} />
                </ListItem>
                <Box px={3}>
                  <Range
                    step={subject.rangeStep}
                    min={subject.min}
                    max={subject.max}
                    from={currentValue?.from || subject.min}
                    to={currentValue?.to || subject.max}
                    disabled={!isSubjectChecked}
                    getAriaValueText={(value: number) => `${subject.label} range: ${value}`}
                    onChange={(value: number[]): void => {
                      handleRangeChange(subject.name, value)
                    }}
                  />
                </Box>
              </Box>
            )
          })}
      </Box>
    </Box>
  )
}

export default withContext(TestScoreSelectorWithSubjects)
